import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaService } from '../../../quotas/services/quota.service';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';
import { QuotaAssignment } from '../../../quotas/models/assignment.interface';

export interface AddVolumeDialogData {
  assignment: QuotaAssignment;
}

import { splitApiErrors } from '../../../../core/utils/api-format';

@Component({
  selector: 'app-add-volume-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './add-volume-dialog.component.html',
  styleUrls: ['./add-volume-dialog.component.css']
})
export class AddVolumeDialogComponent {
  private quotaService = inject(QuotaService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<AddVolumeDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  data = inject<AddVolumeDialogData>(MAT_DIALOG_DATA);

  loading = signal(false);
  errorMessage = signal('');
  fieldErrors = signal<Record<string, string>>({});

  organizationInn = '';
  personIdnp = '';
  deliveredVolume: number | null = null;

  onKeyDown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (!allowed.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onNumericInput(event: Event, field: 'personIdnp' | 'organizationInn'): void {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '').slice(0, 14);
    input.value = filtered;
    this[field] = filtered;
    const errors = { ...this.fieldErrors() };
    delete errors[field];
    this.fieldErrors.set(errors);
  }

  isFormValid(): boolean {
    return !!(
      this.organizationInn.length === 14 &&
      this.personIdnp.length === 14 &&
      this.deliveredVolume &&
      this.deliveredVolume > 0
    );
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');
    this.fieldErrors.set({});


    const signatureDialogRef = this.dialog.open(OepSignatureDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organizationInn: this.organizationInn,
        personIdnp: this.personIdnp,
        title: this.translate.instant('addVolumeDialog.signatureTitle')
      } as OepSignatureDialogData
    });

    signatureDialogRef.afterClosed().subscribe(result => {
      if (result?.success && result.token) {
        this.addVolume(result.token);
      } else if (result?.fieldErrors) {
        this.fieldErrors.set(result.fieldErrors);
      }
    });
  }

  private addVolume(token: string): void {
    this.loading.set(true);


    const deliveredVolumeInKg = this.deliveredVolume! * 1000;

    this.quotaService.addVolumeFulfillment({
      deliveredVolume: deliveredVolumeInKg,
      organizationInn: this.organizationInn,
      personIdnp: this.personIdnp,
      quotaAssignmentId: this.data.assignment.id,
      token: token
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open(this.translate.instant('addVolumeDialog.success'), 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          this.handleApiErrors(response);
        }
      },
      error: (error) => {
        console.error('Error adding volume:', error);
        this.handleApiErrors(error.error || {});
        this.loading.set(false);
      }
    });
  }

  private handleApiErrors(response: { msg?: string; res?: Array<{ field: string; ru: string }> }): void {
    const { fieldErrors, message } = splitApiErrors(response, this.translate.instant('addVolumeDialog.error'));
    if (Object.keys(fieldErrors).length) {
      this.fieldErrors.set(fieldErrors);
    }
    if (message) {
      this.errorMessage.set(message);
    }
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
